/**
 * Receivables — money lent out (FR-RCV-01 to FR-RCV-08).
 *
 * Pure. The interesting part is not the CRUD, it is **FR-RCV-05**: three of the eight
 * balances in the workbook are not figures at all but running arithmetic strings, and
 * each term is a real event that happened on a real day.
 *
 *     Shiva          6000+3000+6000-8000+20000-20000              → 7,000 over 6 entries
 *     Msha           30000-5000-5000                              → 20,000 over 3
 *     Sweeping Loan  600-500+5000-100-500-500-500-500-500-500     → 2,000 over 10
 *
 * Importing those as net figures would throw away the lending history and leave the
 * outstanding balance as a number nobody could explain. So each term becomes an entry: a
 * positive term is money lent, a negative term a repayment (FR-RCV-02, which requires the
 * outstanding figure to be **derived** and never hand-typed).
 *
 * **FR-RCV-05a — read the August sheet only.** The block is repeated on all eight month
 * sheets and grows by one term each month: Sweeping Loan runs to four terms in January
 * and ten in August. Summing across sheets would count the same lending eight times. This
 * is the same class of defect as importing by sheet name rather than row date
 * (FR-IMP-02), and it has already caught this project out once — an earlier draft of
 * `CLAUDE.md` quoted July's nine-term expression against August's figure.
 */

import type { DateKey } from './dates';
import { evaluateExpression } from './expression';
import type { Paise } from './money';

export type ReceivableStatus = 'outstanding' | 'settled';

export interface ReceivableEntry {
  /** Positive: lent out. Negative: repaid to us. */
  amountPaise: Paise;
  /**
   * Null for the reconstructed entries — the workbook records the arithmetic but not the
   * dates, so they go to the review queue rather than being given invented ones
   * (FR-IMP-11).
   */
  dateKey: DateKey | null;
  note: string | null;
}

export interface Receivable {
  id: string;
  person: string;
  note: string | null;
  entries: ReceivableEntry[];
  archived: boolean;
  /** Where the figure came from, so a reconstructed balance is never mistaken for a
   *  dated one. */
  source: 'reconstructed' | 'stated' | 'entered';
}

export class ReceivableError extends Error {}

// ---------------------------------------------------------------------------
// Deriving the position
// ---------------------------------------------------------------------------

/** FR-RCV-02: outstanding is derived from the entries, never stored. */
export function outstandingPaise(receivable: Pick<Receivable, 'entries'>): Paise {
  return receivable.entries.reduce((sum, entry) => sum + entry.amountPaise, 0);
}

export function lentPaise(receivable: Pick<Receivable, 'entries'>): Paise {
  return receivable.entries
    .filter((e) => e.amountPaise > 0)
    .reduce((sum, e) => sum + e.amountPaise, 0);
}

export function repaidPaise(receivable: Pick<Receivable, 'entries'>): Paise {
  return -receivable.entries
    .filter((e) => e.amountPaise < 0)
    .reduce((sum, e) => sum + e.amountPaise, 0);
}

/**
 * Settled when nothing is left owing.
 *
 * A negative balance — repaid more than was lent — is also "settled" rather than an
 * error: Shiva's row nets a `+20000-20000` pair, and an over-repayment in the source is
 * a fact to record, not a validation failure.
 */
export function statusOf(receivable: Pick<Receivable, 'entries'>): ReceivableStatus {
  return outstandingPaise(receivable) <= 0 ? 'settled' : 'outstanding';
}

/** Entries with no date — the review queue's worklist (FR-IMP-11). */
export function undatedEntries(receivable: Pick<Receivable, 'entries'>): ReceivableEntry[] {
  return receivable.entries.filter((e) => e.dateKey === null);
}

export function totalOutstandingPaise(receivables: Pick<Receivable, 'entries' | 'archived'>[]): Paise {
  return receivables
    .filter((r) => !r.archived)
    .reduce((sum, r) => sum + Math.max(0, outstandingPaise(r)), 0);
}

// ---------------------------------------------------------------------------
// FR-RCV-05 — reconstructing a running expression
// ---------------------------------------------------------------------------

/**
 * Split a `600-500+5000-100` chain into its signed terms.
 *
 * Each term is evaluated through the shared parser rather than by `Number()`, so a term
 * that is itself an expression is handled and a malformed one is rejected the same way it
 * would be anywhere else in the app (AD-05 — never `eval`).
 *
 * Returns null rather than throwing: the caller is import, and a bad expression should
 * queue for review, not abort a migration.
 */
export function splitTerms(expression: string): Paise[] | null {
  const trimmed = expression.trim();
  if (!trimmed) return null;

  // Only a flat +/- chain is reconstructible into events. Anything with brackets or
  // multiplication is a calculation rather than a history, and is left to the ordinary
  // amount parser.
  if (/[()*/]/.test(trimmed)) return null;

  const terms: Paise[] = [];
  // Leading sign, then alternating operator and operand.
  const pattern = /([+-]?)\s*([\d.]+)/g;
  let consumed = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(trimmed)) !== null) {
    if (match.index !== consumed) return null; // a gap means something unparsed
    consumed = pattern.lastIndex;

    const value = evaluateExpression(match[2]);
    if (!value.ok) return null;
    terms.push(match[1] === '-' ? -value.paise : value.paise);

    // Skip the whitespace the next iteration would otherwise trip over.
    while (consumed < trimmed.length && trimmed[consumed] === ' ') consumed += 1;
  }

  if (consumed !== trimmed.length || terms.length === 0) return null;
  return terms;
}

/**
 * Turn a workbook expression into a receivable's entries.
 *
 * Undated by construction — the source has the arithmetic and not the calendar. The
 * derived total is checked against the figure the workbook displays, and a mismatch is
 * reported rather than silently accepted: that check is what would have caught the
 * July-versus-August confusion of FR-RCV-05a.
 */
export function reconstruct(
  person: string,
  expression: string,
  expectedPaise: Paise,
  note: string | null = null,
): { receivable: Receivable; problem: string | null } {
  const terms = splitTerms(expression);

  if (!terms) {
    return {
      receivable: {
        id: slug(person), person, note, entries: [], archived: false, source: 'reconstructed',
      },
      problem: `Could not read "${expression}" as a run of additions and subtractions.`,
    };
  }

  const receivable: Receivable = {
    id: slug(person),
    person,
    note,
    entries: terms.map((amountPaise) => ({ amountPaise, dateKey: null, note: null })),
    archived: false,
    source: 'reconstructed',
  };

  const derived = outstandingPaise(receivable);
  return {
    receivable,
    problem:
      derived === expectedPaise
        ? null
        : `${person}: the expression derives ${derived / 100} but the sheet shows ${
            expectedPaise / 100
          }. Check the month sheet — the block is cumulative and only August is current.`,
  };
}

export function slug(person: string): string {
  return person
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

// ---------------------------------------------------------------------------
// The seeded position (FR-RCV-04, FR-RCV-05)
// ---------------------------------------------------------------------------

interface SeedSpec {
  person: string;
  /** Whole rupees, as the sheet shows them. */
  amount: number;
  expression?: string;
  note?: string;
}

/**
 * The eight receivables, read from the **August** sheet (FR-RCV-05a).
 *
 * Five are plain figures with no date and no history in the source; three carry running
 * expressions and reconstruct into dated-once-reviewed entries.
 */
export const SEED_RECEIVABLES: SeedSpec[] = [
  { person: 'PK', amount: 50_000 },
  {
    person: 'Msha',
    amount: 20_000,
    expression: '30000-5000-5000',
  },
  { person: 'Land Reg', amount: 10_000 },
  {
    person: 'Shiva',
    amount: 7_000,
    expression: '6000+3000+6000-8000+20000-20000',
    note: 'Watch and Airpods',
  },
  { person: 'Sofa LL', amount: 3_000 },
  {
    person: 'Sweeping Loan',
    amount: 2_000,
    expression: '600-500+5000-100-500-500-500-500-500-500',
  },
  { person: 'Thalapathi Books', amount: 1_500 },
  { person: 'Thara Loan', amount: 1_500 },
];

export interface SeedResult {
  receivables: Receivable[];
  problems: string[];
}

/**
 * Build the seeded receivables.
 *
 * The five plain rows become a single undated entry each — the workbook records what is
 * owed but not when it was lent, and inventing a date would make a guess look like a
 * record. They queue for dating alongside the reconstructed ones.
 */
export function buildSeedReceivables(specs: SeedSpec[] = SEED_RECEIVABLES): SeedResult {
  const receivables: Receivable[] = [];
  const problems: string[] = [];

  for (const spec of specs) {
    const expectedPaise = spec.amount * 100;

    if (spec.expression) {
      const { receivable, problem } = reconstruct(
        spec.person,
        spec.expression,
        expectedPaise,
        spec.note ?? null,
      );
      receivables.push(receivable);
      if (problem) problems.push(problem);
      continue;
    }

    receivables.push({
      id: slug(spec.person),
      person: spec.person,
      note: spec.note ?? null,
      entries: [{ amountPaise: expectedPaise, dateKey: null, note: 'Position as at 1 Aug 2026' }],
      archived: false,
      source: 'stated',
    });
  }

  const total = totalOutstandingPaise(receivables);
  if (total !== 95_000_00) {
    problems.push(`Seeded receivables total ${total / 100}, not the expected 95,000.`);
  }

  return { receivables, problems };
}
